import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import type { Prompt } from '../types';

interface PromptDisplayProps {
  prompt: Prompt;
  copyTimeout?: number;
}

// Constants
const DEFAULT_COPY_TIMEOUT = 2000;

// Utility functions
function decodeBase64ToUtf8(base64Data: string): string {
  try {
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  } catch (error) {
    throw new Error(
      `Failed to decode base64 data: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

function unescapeString(str: string): string {
  return str
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r')
    .replace(/\\\\/g, '\\');
}

function formatContent(decoded: string): string {
  try {
    const parsed = JSON.parse(decoded);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return decoded;
  }
}

// Custom hook for copy functionality
function useCopyToClipboard(text: string, timeout = DEFAULT_COPY_TIMEOUT) {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, timeout);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Could show a toast notification here
    }
  }, [text, timeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { isCopied, copy };
}

export const PromptDisplay: React.FC<PromptDisplayProps> = ({
  prompt,
  copyTimeout = DEFAULT_COPY_TIMEOUT,
}) => {
  // Memoize the decoded content
  const decodedContent = useMemo(() => {
    try {
      const decoded = decodeBase64ToUtf8(prompt.prompt.data);
      const formatted = formatContent(decoded);
      return unescapeString(formatted);
    } catch (error) {
      console.error('Failed to decode prompt:', error);
      return `Error: Unable to decode prompt content\n\nDetails: ${error instanceof Error ? error.message : String(error)}`;
    }
  }, [prompt.prompt.data]);

  const { isCopied, copy } = useCopyToClipboard(decodedContent, copyTimeout);

  return (
    <div className="space-y-3">
      {/* Header with copy button */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm text-foreground" id={`prompt-${prompt.promptName}`}></h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={copy}
          className="h-8 px-2 text-xs"
          aria-label={`Copy ${prompt.promptName} content to clipboard`}
        >
          {isCopied ? (
            <>
              <Check className="w-3 h-3 mr-1" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-3 h-3 mr-1" />
              Copy
            </>
          )}
        </Button>
      </div>

      {/* Content display - always expanded */}
      <div className="relative">
        <pre
          className="text-sm bg-muted/50 border rounded-md p-3 whitespace-pre-wrap font-mono text-foreground transition-all duration-300 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent max-h-[80vh] overflow-y-auto overflow-x-auto"
          role="region"
          aria-labelledby={`prompt-${prompt.promptName}`}
          aria-live="polite"
        >
          {decodedContent}
        </pre>
      </div>
    </div>
  );
};
